import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useGetDistributorCompaniesQuery } from 'api/main';
import { ORDER_BY } from 'utils/constants';
import { distributorSchemas } from 'schemas';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomSelect, Loader } from 'components';
import { Box, Collapse, MenuItem, Paper } from '@mui/material';

const getDefaultFilters = obj => ({
    companyId: obj?.companyId,
});

const buildFilterPayload = filters => ({
    ...filters,
});

const DistributorUserFilters = ({ open, applyFilters, savedFilters, loading, defaultFilters }) => {
    const { data: { distributors = [] } = { distributors: [] }, isLoading: companyLoader } =
        useGetDistributorCompaniesQuery(
            distributorSchemas.getDistributorList({
                pagination: false,
                orderBy: ORDER_BY.distributorCompanyName,
                oneOrMoreConventionsFullySigned: true,
            })
        );

    const companyIdValue = useMemo(() => {
        if (Array.isArray(savedFilters.companyId)) {
            return savedFilters.companyId[0] || 'all';
        }
        return savedFilters.companyId || 'all';
    }, [savedFilters.companyId]);

    const handleApplyFilters = useCallback(() => {
        applyFilters(buildFilterPayload(savedFilters));
    }, [applyFilters, savedFilters]);

    const handleCancel = useCallback(() => {
        const defaults = getDefaultFilters(defaultFilters);
        applyFilters(buildFilterPayload(defaults));
    }, [applyFilters, defaultFilters]);

    const handleChange = useCallback(
        key => e => {
            const value = e.target.value;
            applyFilters({
                ...savedFilters,
                [key]: value === 'all' ? undefined : [value],
            });
        },
        [applyFilters, savedFilters]
    );

    return (
        <Collapse in={open} sx={{ mb: 4 }}>
            <Paper elevation={1} sx={{ padding: 2 }}>
                <Loader loading={companyLoader}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2 }}>
                        <CustomSelect
                            value={companyIdValue}
                            label={texts.PARTNER}
                            onChange={handleChange('companyId')}
                            sx={{ mb: 0 }}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {distributors.map(({ 0: { id, corporateName } }) => (
                                <MenuItem key={id} value={id}>
                                    {corporateName}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Box>
                    <Box textAlign="right">
                        <CancelButton disabled={loading} onClick={handleCancel}>
                            {texts.CANCEL}
                        </CancelButton>
                        <ActionButton
                            loading={loading}
                            disabled={loading}
                            onClick={handleApplyFilters}
                        >
                            {texts.APPLY}
                        </ActionButton>
                    </Box>
                </Loader>
            </Paper>
        </Collapse>
    );
};

DistributorUserFilters.propTypes = {
    open: PropTypes.bool,
    applyFilters: PropTypes.func,
    savedFilters: PropTypes.object,
    defaultFilters: PropTypes.object,
    loading: PropTypes.bool,
};

export default DistributorUserFilters;
